import { type ReactNode, useCallback, useRef, useState } from 'react';
import {
  useFloating,
  flip,
  shift,
  autoUpdate,
  type Placement,
  offset,
} from '@floating-ui/react-dom';
import { Transition } from '@headlessui/react';
import classNames from 'classnames';
import { Link } from '@/shared/components/atoms/Link';
import useDocumentEventListener from '@/shared/hooks/useDocumentEventListener';
import BoltIcon from '@/shared/icons/flip-ui-kit/small/Bolt';
import { Key } from '@/shared/utils/events';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  onShow?: () => void;
  onHide?: () => void;
  childrenClassName?: string;
  tooltipClassName?: string;
  pointer?: boolean;
  tabable?: boolean;
  placement?: Placement;
  disabledContent?: ReactNode;
  // advanced tooltip props
  title?: string;
  icon?: ReactNode;
  link?: string;
  linkLabel?: string;
  fullWidth?: boolean;
}

export function Tooltip({
  content,
  children,
  disabled = false,
  onShow,
  onHide,
  childrenClassName,
  tooltipClassName,
  pointer = true,
  tabable = true,
  placement = 'top',
  disabledContent,
  title,
  icon,
  link,
  linkLabel,
  fullWidth = false,
}: TooltipProps): React.JSX.Element {
  const [isActive, setIsActive] = useState(false);
  const [escHit, setEscHit] = useState(false);
  const appearTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { floatingStyles, refs } = useFloating({
    open: isActive,
    placement,
    middleware: [offset(8), flip(), shift({ padding: 16 })],
    whileElementsMounted: autoUpdate,
  });

  const handleFocus = () => {
    if (appearTimeout.current) clearTimeout(appearTimeout.current);
    appearTimeout.current = setTimeout(() => {
      setIsActive(true);
    }, 0);
  };

  const handleBlur = () => {
    if (appearTimeout.current) clearTimeout(appearTimeout.current);
    setEscHit(false);
    setIsActive(false);
  };

  const listener = useCallback(
    (event: KeyboardEvent) => {
      if (isActive && event.key === Key.Escape) {
        setEscHit(true);
      }
    },
    [isActive],
  );
  useDocumentEventListener('keydown', listener);
  return (
    <div
      onPointerOver={handleFocus}
      onPointerEnter={handleFocus}
      onPointerLeave={handleBlur}
      className={classNames(fullWidth && 'w-full')}
    >
      <div
        tabIndex={tabable ? 0 : -1}
        role="button"
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={classNames(
          !disabled && pointer ? 'cursor-pointer' : 'cursor-default',
          childrenClassName,
        )}
        ref={refs.setReference}
      >
        {children}
      </div>

      <Transition
        appear
        show={isActive && !escHit && (!disabled || Boolean(disabledContent))}
        role="tooltip"
        as="div"
        className="z-[900] transition-opacity"
        enter="ease-out duration-300"
        enterFrom="opacity-0 z-20"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        beforeEnter={onShow}
        afterLeave={onHide}
        ref={refs.setFloating}
        style={floatingStyles}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={classNames(
            'flex max-w-[306px] flex-col gap-y-2 rounded-md border border-cf-gray-5 bg-cf-gray-4 p-2 text-left font-aeonikRegular',
            tooltipClassName,
          )}
        >
          {title && (
            <>
              <div
                className={classNames(
                  `flex items-center gap-x-1 font-aeonikMedium text-14 text-white`,
                )}
              >
                {icon || <BoltIcon />}
                <span>Title</span>
              </div>
              <div className="w-full border-b border-b-[#2B2B2B]" />
            </>
          )}
          <div
            className={classNames('flex items-center whitespace-pre-wrap text-12 text-cf-light-3')}
            style={{
              wordBreak: 'break-word',
            }}
          >
            {disabled ? disabledContent : content}
          </div>
          {link && (
            <Link href={link} color="green" className="text-12" target="_blank">
              {linkLabel || 'Learn more'}
            </Link>
          )}
        </div>
      </Transition>
    </div>
  );
}
